import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import type { NotificationChannelDto } from '@ops-monitor/shared';

import { admins, channels } from '../api/client';
import { Button, ErrorState, Field, Spinner } from '../components/ui';

/**
 * «Настройки» — two sections on one page:
 *  - «Администраторы»: the admin table IS the authorization model — a listed
 *    e-mail can receive a login code; removing a row locks the person out on
 *    their next request. The last admin cannot be removed (server-enforced).
 *  - «Каналы уведомлений» (2026-10-30): webhook-based alert delivery targets.
 *    A channel is a SLACK INCOMING WEBHOOK (the only type today); the alert
 *    engine posts the same payload to every channel a rule selects, falling
 *    back to the default channel, then to the legacy SLACK_WEBHOOK_URL env.
 *    The default channel itself is seeded from that env var at first boot.
 *    Exactly one channel is default (server-enforced); deleting a channel can
 *    never strand a rule — rules resolve their targets at send time and skip
 *    unknown ids.
 */

/** «https://hooks.slack.com/services/T0…/B0…/AbC123» → «•••••• AbC123». */
function maskUrl(url: string): string {
  if (!url) return '';
  return `•••••• ${url.slice(-6)}`;
}

export function SettingsPage() {
  return (
    <>
      <h1 className="page__title">Настройки</h1>
      <p className="page__subtitle">Администраторы доступа и каналы доставки оповещений. Доступ к странице есть у всех, кто вошёл.</p>
      <ChannelsSection />
      <AdminsSection />
    </>
  );
}

/** Webhook delivery targets: table + create/edit/delete, default-flag enforced server-side. */
function ChannelsSection() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['channels'], queryFn: ({ signal }) => channels.list(signal) });
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['channels'] });
  };

  const create = useMutation({
    mutationFn: () => channels.create({ name: name.trim(), webhookUrl: url.trim(), isDefault }),
    onSuccess: () => {
      setName('');
      setUrl('');
      setIsDefault(false);
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message.replace(/.*HTTP \d+: /, '') : 'Не удалось создать канал'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => channels.remove(id),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message.replace(/.*HTTP \d+: /, '') : 'Не удалось удалить канал'),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    if (!name.trim() || !url.trim()) setError('Нужны название и URL вебхука');
    else create.mutate();
  };

  return (
    <section style={{ marginBottom: 40 }}>
      <h2 className="section-block__title">Каналы уведомлений</h2>
      <p className="page__subtitle">
        Канал — это Slack incoming webhook: адрес, в который движок оповещений шлёт сообщения о проблемах и восстановлениях.
        Правило может доставляться в несколько каналов; без выбора используется канал по умолчанию (★, при первом запуске он
        создан из SLACK_WEBHOOK_URL).
      </p>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', maxWidth: 720, margin: '0 0 20px' }}>
        <Field label="Название" htmlFor="channel-name" hint="Например: дежурные, инфра">
          <input id="channel-name" type="text" value={name} onChange={(event) => setName(event.target.value)} placeholder="дежурные" />
        </Field>
        <Field label="Webhook URL" htmlFor="channel-url" hint="https://hooks.slack.com/services/…">
          <input id="channel-url" type="text" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://hooks.slack.com/services/…" />
        </Field>
        <label className="alert-box__radio" style={{ paddingBottom: 10 }}>
          <input type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />
          по умолчанию
        </label>
        <Button variant="primary" type="submit" loading={create.isPending} style={{ marginBottom: 10 }}>
          Добавить
        </Button>
      </form>
      {error ? <p className="form-error">{error}</p> : null}

      {list.isPending ? <Spinner label="Загружаю каналы" /> : null}
      {list.isError ? <ErrorState message="Не удалось загрузить каналы" /> : null}
      {list.data && list.data.items.length > 0 ? (
        <table className="data-table" style={{ maxWidth: 760, marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Название</th>
              <th>Тип</th>
              <th>Webhook</th>
              <th>По умолчанию</th>
              <th>Создан</th>
              <th aria-label="Действия" />
            </tr>
          </thead>
          <tbody>
            {list.data.items.map((channel) => (
              <ChannelRow key={channel.id} channel={channel} onDeleted={() => remove.mutate(channel.id)} deleting={remove.isPending && remove.variables === channel.id} onChanged={invalidate} onError={setError} />
            ))}
          </tbody>
        </table>
      ) : list.isSuccess ? (
        <p className="page__subtitle">каналов нет — оповещения уйдут в SLACK_WEBHOOK_URL из окружения</p>
      ) : null}
    </section>
  );
}

/** One channel row: read view + inline edit (URL shown masked until edited). */
function ChannelRow({
  channel,
  onDeleted,
  deleting,
  onChanged,
  onError,
}: {
  channel: NotificationChannelDto;
  onDeleted: () => void;
  deleting: boolean;
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(channel.name);
  const [url, setUrl] = useState('');
  const [isDefault, setIsDefault] = useState(channel.isDefault);

  const update = useMutation({
    mutationFn: () => {
      const input: { name?: string; webhookUrl?: string; isDefault: boolean } = { isDefault };
      if (name.trim() && name.trim() !== channel.name) input.name = name.trim();
      if (url.trim()) input.webhookUrl = url.trim();
      return channels.update(channel.id, input);
    },
    onSuccess: () => {
      setEditing(false);
      setUrl('');
      onError('');
      onChanged();
    },
    onError: (err) => onError(err instanceof Error ? err.message.replace(/.*HTTP \d+: /, '') : 'Не удалось сохранить канал'),
  });

  if (!editing) {
    return (
      <tr>
        <td>
          {channel.name}
          {channel.isDefault ? ' ★' : ''}
        </td>
        <td>{channel.type}</td>
        <td className="admin-row__meta">{maskUrl(channel.webhookUrl)}</td>
        <td>{channel.isDefault ? 'да' : '—'}</td>
        <td>{new Date(channel.createdAt).toLocaleDateString('ru-RU')}</td>
        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            Изменить
          </Button>{' '}
          <Button variant="danger" size="sm" loading={deleting} onClick={onDeleted}>
            Удалить
          </Button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td>
        <input type="text" value={name} onChange={(event) => setName(event.target.value)} aria-label="Название канала" style={{ width: 140 }} />
      </td>
      <td>
        <select value={channel.type} onChange={() => undefined} aria-label="Тип канала">
          <option value="slack">slack</option>
        </select>
      </td>
      <td>
        <input
          type="text"
          value={url}
          placeholder={maskUrl(channel.webhookUrl)}
          onChange={(event) => setUrl(event.target.value)}
          aria-label="Webhook URL (пусто = не менять)"
          style={{ width: 240 }}
        />
      </td>
      <td>
        <label className="alert-box__radio">
          <input type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} />
          по умолчанию
        </label>
      </td>
      <td>{new Date(channel.createdAt).toLocaleDateString('ru-RU')}</td>
      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        <Button variant="primary" size="sm" loading={update.isPending} onClick={() => update.mutate()}>
          Сохранить
        </Button>{' '}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditing(false);
            setUrl('');
            setName(channel.name);
            setIsDefault(channel.isDefault);
          }}
        >
          Отмена
        </Button>
      </td>
    </tr>
  );
}

/** The authorization model: who may receive a login code. */
function AdminsSection() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: ['admins'], queryFn: ({ signal }) => admins.list(signal) });
  const audit = useQuery({ queryKey: ['admins-audit'], queryFn: ({ signal }) => admins.audit(signal), refetchInterval: 60_000 });
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admins'] });
    void queryClient.invalidateQueries({ queryKey: ['admins-audit'] });
  };

  const add = useMutation({
    mutationFn: () => admins.add(email.trim().toLowerCase()),
    onSuccess: () => {
      setEmail('');
      setError(null);
      invalidate();
    },
    onError: (err) => setError(err instanceof Error ? err.message.replace(/.*HTTP \d+: /, '') : 'Не удалось добавить'),
  });
  const remove = useMutation({
    mutationFn: (target: string) => admins.remove(target),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message.replace(/.*HTTP \d+: /, '') : 'Не удалось удалить'),
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    if (email.includes('@')) add.mutate();
    else setError('Введите e-mail');
  };

  if (list.isPending) return <Spinner label="Загружаю администраторов" />;
  if (list.isError) return <ErrorState message="Не удалось загрузить список" />;

  return (
    <section>
      <h2 className="section-block__title">Администраторы</h2>
      <p className="page__subtitle">Кому можно присылать код входа. Удалённый администратор теряет доступ сразу — на следующем запросе.</p>
      <form onSubmit={submit} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', maxWidth: 460, margin: '0 0 20px' }}>
        <Field label="Добавить администратора" htmlFor="admin-email" hint="Новый администратор сразу может запросить код">
          <input
            id="admin-email"
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@ismart.org"
          />
        </Field>
        <Button variant="primary" type="submit" loading={add.isPending}>
          Добавить
        </Button>
      </form>
      {error ? <p className="form-error">{error}</p> : null}

      <div style={{ maxWidth: 560 }}>
        {list.data.items.map((admin) => (
          <div key={admin.email} className="admin-row">
            <div>
              <div className="admin-row__email">{admin.email}</div>
              <div className="admin-row__meta">
                добавил: {admin.addedBy} · {new Date(admin.createdAt).toLocaleDateString('ru-RU')}
              </div>
            </div>
            <Button
              variant="danger"
              size="sm"
              loading={remove.isPending && remove.variables === admin.email}
              onClick={() => remove.mutate(admin.email)}
            >
              Удалить
            </Button>
          </div>
        ))}
      </div>

      <h3 className="section-block__title" style={{ marginTop: 32 }}>
        Журнал событий
      </h3>
      {audit.data && audit.data.items.length > 0 ? (
        <table className="data-table" style={{ maxWidth: 640 }}>
          <thead>
            <tr>
              <th>Когда</th>
              <th>Событие</th>
              <th>E-mail</th>
              <th>Детали</th>
            </tr>
          </thead>
          <tbody>
            {audit.data.items.slice(0, 30).map((event) => (
              <tr key={event.id}>
                <td>{new Date(event.createdAt).toLocaleString('ru-RU')}</td>
                <td>{event.kind}</td>
                <td>{event.email ?? '—'}</td>
                <td>{event.details ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="page__subtitle">пока пусто</p>
      )}
    </section>
  );
}
